import { useUpdateOrgMutation } from 'store/slices/apiV1/org';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { handleErr } from 'utils/functions';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import { FormControl, FormHelperText, GridLegacy, Stack } from '@mui/material';
import {
  HookFormTeamTypeAutocomplete,
  HookFormToggle,
} from 'ui-component/HookFormComponents';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { yupResolver } from '@hookform/resolvers/yup';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import useSnackbar from 'hooks/useSnackbar';
import {
  formConstants,
  formValidation,
} from 'views/properties/Teams/TeamPreferences/constants';
import { gridSpacing } from 'constants/themeConstants';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import { OrgPatch } from 'types/org';

type TeamPreferencesFieldValuesType = Pick<
  OrgPatch,
  'defaultTeamType' | 'allowNoTeamShipments'
>;

const TeamPreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const {
    activeOrgId,
    org,
    defaultTeamTypeId,
    handleReplaceWithCustomTeamName,
  } = useCustomTeamTypeName();
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();

  const defaultValues = {
    [formConstants.allowNoTeamShipments.name]: org?.allowNoTeamShipments,
    [formConstants.defaultTeamType.name]: defaultTeamTypeId ?? '',
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    setValue,
  } = useForm<TeamPreferencesFieldValuesType>({
    defaultValues,
    resolver: yupResolver(formValidation),
    mode: 'all',
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (org) {
      reset(defaultValues);
    }
  }, [org]);

  const onSubmit = async (payload: TeamPreferencesFieldValuesType) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload,
        }).unwrap();
        dispatchSuccessSnackbar(
          handleReplaceWithCustomTeamName('Team Preferences updated')
        );
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardSectionWithAction
        contents={
          <FormControl fullWidth>
            <GridLegacy container spacing={gridSpacing}>
              <GridLegacy item xs={12}>
                <HookFormToggle
                  name={formConstants.allowNoTeamShipments.name}
                  label={handleReplaceWithCustomTeamName(
                    formConstants.allowNoTeamShipments.label
                  )}
                  control={control}
                  errors={errors}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <Stack spacing={1}>
                  <HookFormTeamTypeAutocomplete
                    name={formConstants.defaultTeamType.name}
                    label={handleReplaceWithCustomTeamName(
                      formConstants.defaultTeamType.label
                    )}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    defaultValue={
                      defaultValues[
                        formConstants.defaultTeamType.name
                      ] as string
                    }
                    disabled={!isEditing}
                  />
                  <FormHelperText>
                    The default team name will be used throughout the Cofactr
                    platform in place of &apos;Team&apos;.
                  </FormHelperText>
                </Stack>
              </GridLegacy>
            </GridLegacy>
          </FormControl>
        }
        action={
          isEditing ? (
            <SegmentLoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              loading={isUpdatingOrg}
              permissionScope={{
                app: ALL_APPS.KITTING.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              {isDirty ? 'Save' : 'Cancel'}
            </SegmentLoadingButton>
          ) : (
            <SegmentButton
              type="button"
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ float: 'right' }}
              permissionScope={{
                app: ALL_APPS.KITTING.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              Edit
            </SegmentButton>
          )
        }
        title={handleReplaceWithCustomTeamName('Team Preferences')}
      />
    </form>
  );
};

export default TeamPreferences;
